import { guid } from 'app/shared/lib/guid';
import { sleep } from 'app/shared/lib/utilities';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { autoinject } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IStorageSubscription } from 'app/shared/lib/client-storage';
import { AppSession } from 'app/shared/lib/essette-api';
import { asc, ensureArray, sortArray } from 'app/shared/lib/arrays';

export interface IWindowState {
    readonly id: string;
    readonly url: string;
    readonly hidden: boolean;
    readonly focused: boolean;
    readonly closed: boolean;
    readonly started: number;
    readonly updated: number;
    readonly activity: number;
    readonly clockRate: number;
}

const defaultWindowState: IWindowState = {
    id: '', url: '', hidden: false, focused: false, closed: false, started: 0, updated: 0, activity: 0, clockRate: 1
};

const CLOSED_WINDOW_EXPIRE_MILLISECONDS = 10_000; // 10 seconds
const MISSING_WINDOW_EXPIRE_MILLISECONDS = 14_400_000; // 4 hours
const SYNC_INTERVAL_MILLISECONDS = 10_000; // 10 seconds
const SYNC_IMMEDIATE_MILLISECONDS = 1_000; // 1 second
const STORAGE_KEY = 'windows';

/**
 * Handle synchronizing state between multiple windows/tabs in the browser
 */
@autoinject
export class SyncService {

    /**
     * Is the current window the active window?
     */
    public isActive: boolean = false;

    /**
     * Represents the current window (the window executing this code)
     */
    public currentWindow: IWindowState;

    /**
     * Represents the window with the most recent user interaction
     */
    public activeWindow: IWindowState | null = null;

    /**
     * All windows for the application
     */
    public allWindows: IWindowState[] = [];

    private readonly windowId: string;
    private readonly started: number;
    private lastReadTime: number;
    private lastWriteTime: number;
    private isClosed: boolean = false;
    private isTerminated: boolean = false;
    private storageSubscription: IStorageSubscription | null = null;

    constructor(private readonly env: EnvironmentService, private readonly session: AppSession, private readonly logger: AppLogger) {
        this.logger.source = this;
        this.logger.target = 'console';

        this.windowId = guid();
        this.lastReadTime = this.lastWriteTime = this.started = Date.now();

        this.currentWindow = {
            id: this.windowId,
            url: window.location.hash,
            hidden: window.document.hidden || false,
            focused: window.document.hasFocus(),
            closed: this.isClosed,
            started: this.started,
            updated: this.started,
            activity: this.started,
            clockRate: 1
        }

        this.start();
    }

    /**
     * Shut down the synchronization service
     */
    public terminate(): void {
        if(this.isTerminated) { return; }

        this.logger.info(`Terminating Current Window (${this.currentWindow.url})`, this.currentWindow);
        this.isClosed = true;

        if(this.storageSubscription) {
            this.storageSubscription.dispose();
            this.storageSubscription = null;
        }

        this.update(false, 0);

        this.isTerminated = true;
    }

    /**
     * Update the state of the current window
     * @param activityDetected set to `true` if there has been activity on the current window
     */
    public update(activityDetected: boolean = false, clockRate?: number): void {
        const time = Date.now();
        const lastActivity = this.currentWindow.activity;

        // update current window
        this.currentWindow = {
            id: this.windowId,
            url: window.location.hash,
            hidden: window.document.hidden || false,
            focused: window.document.hasFocus(),
            closed: this.isClosed,
            started: this.started,
            updated: time,
            activity: activityDetected ? time : lastActivity,
            clockRate: clockRate ?? this.currentWindow.clockRate
        };

        // replace the current window in the list
        this.replaceCurrentWindow();

        if(this.isClosed) {
            // immediately sync closed state
            this.writeState();
        }
        else if(activityDetected && (time - this.lastWriteTime) > SYNC_IMMEDIATE_MILLISECONDS) {
            // immediately sync new activity so there isn't a delay in keeping other windows unlocked
            this.writeState();
        }
    }

    /**
     * Force loading the Sync status from storage immediately
     */
    public refresh(): void {
        this.readState();
    }

    /**
     * Start the synchronization service
     */
    private start(): void {
        this.logger.debug('Starting Window Synchronization', this.currentWindow);
        this.storageSubscription = this.env.storage.subscribe<IWindowState[]>(STORAGE_KEY, (e) => {
            this.readState(e.newValue);
            //this.logger.debug('Receiving Window State', this.currentWindow);
            //this.sync(false, e.newValue)
        });

        // run background process and don't await results
        this.backgroundProcess();

        window.addEventListener('beforeunload', e => {
            this.terminate();

            // check if all windows are closed
            if(this.allWindows.every(w => w.closed)) {
                // clear session
                this.session.shutdown();
                // clear window list
                this.env.storage.remove(STORAGE_KEY, 'local');
            }

            // ensure browser unload still happens
            delete e.returnValue;
        });
    }

    /**
     * Run the background process for this service asynchronously
     */
    private async backgroundProcess(): Promise<void> {
        while(!this.isTerminated) {
            this.readState();
            this.update(false);
            this.writeState();
            //this.sync(true);
            await sleep(SYNC_INTERVAL_MILLISECONDS);
        }
    }

    /**
     * Replace the current window in the window list with the most recent data
     */
    private replaceCurrentWindow(): void {
        const windows = this.allWindows;
        for(let index = 0; index < windows.length; index++) {
            const window = windows[index];
            if(window.id === this.windowId) {
                windows[index] = this.currentWindow;
            }
        }
    }

    /**
     * Read window state from storage
     * @param state storage state if it has already been read, otherwise this function will read it
     */
    private readState(state?: IWindowState[]): void {
        const time = Date.now();
        const readWindows: IWindowState[] = ensureArray(state ?? this.env.storage.getObject(STORAGE_KEY, 'local'));
        const newWindows: IWindowState[] = [];
        let newActive: IWindowState | null = null;

        // add current window if not closed
        if(!this.isClosed) {
            newActive = this.currentWindow;
            newWindows.push(this.currentWindow);
        }

        for(const readWindow of readWindows) {
            // use defaultWindowState to fill out any missing properties from the JSON
            const window = { ...defaultWindowState, ...readWindow };

            if(window.id === this.windowId) {
                // current window is already added at top
                continue;
            }
            const expireTime = window.closed ? CLOSED_WINDOW_EXPIRE_MILLISECONDS : MISSING_WINDOW_EXPIRE_MILLISECONDS;
            if((time - window.updated) >= expireTime) {
                // window is expired, skip it and note a change
                const state = window.closed ? 'Closed' : 'Missing';
                this.logger.debug(`${state} Window State Expired(${window.url}), Removing`, window);
                continue;
            }

            // keep track of window with most recent activity
            if(!newActive || window.activity > newActive.activity) {
                newActive = window;
            }

            // add to new windows list
            newWindows.push(window);
        }

        // sort by age
        sortArray(newWindows, asc(w => w.started));

        const oldActiveId = this.activeWindow?.id || '';
        const newActiveId = newActive?.id || '';

        if(oldActiveId !== newActiveId) {
            // log active window changes
            const oldActiveUrl = this.activeWindow?.url || '';
            const newActiveUrl = newActive?.url || '';

            const url = this.activeWindow && this.activeWindow.url || '';
            this.logger.info(`Active Window Changed(${url})`, {
                oldId: oldActiveId,
                oldUrl: oldActiveUrl,
                newId: newActiveId,
                newUrl: newActiveUrl
            });
        }

        // track results
        this.isActive = (newActiveId === this.windowId);
        this.activeWindow = newActive;
        this.allWindows = newWindows;

        this.lastReadTime = time;
    }

    /**
     * Write the window state to storage
     */
    private writeState(): void {
        // stop storing state once terminated
        if(this.isTerminated) { return; }

        const time = Date.now();

        const writeWindows = [...this.allWindows];
        const readWindows: IWindowState[] = ensureArray(this.env.storage.getObject(STORAGE_KEY, 'local'));

        sortArray(writeWindows, asc(w => w.id));

        if(JSON.stringify(readWindows) !== JSON.stringify(writeWindows)) {
            // changes detected
            if(writeWindows.length) {
                this.env.storage.setObject(STORAGE_KEY, writeWindows, 'local');
            }
            else {
                this.env.storage.remove(STORAGE_KEY, 'local');
            }

            this.lastWriteTime = time;
        }
        else {
            this.logger.debug('No window state changes to write...');
        }

    }

}
